import styled, { keyframes } from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 64px);
  min-height: 100vh;
  margin: 0 0 0 64px;
  background: var(--cco-bg);
  color: var(--cco-ink);

  @media (max-width: 720px) {
    width: 100vw;
    margin: 0;
    padding-bottom: 76px;
  }
`;

export const PageHeader = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
`;

export const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: var(--cco-ink);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
`;

export const PageSub = styled.div`
  margin-top: 3px;
  color: var(--cco-ink-mute);
  font-size: 13px;
`;

export const PageCount = styled.div`
  color: var(--cco-ink-mute);
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  b {
    color: var(--cco-ink);
  }
`;

export const Filters = styled.section`
  display: grid;
  grid-template-columns: 240px 280px 280px auto 1fr;
  border-bottom: 1px solid var(--cco-line);

  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;

    > * {
      border-left: 0 !important;
      border-bottom: 1px solid var(--cco-line);
    }

    > *:last-child {
      border-bottom: 0;
    }
  }
`;

export const FilterCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px var(--cco-pad);

  .k {
    color: var(--cco-ink-mute);
    flex: none;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  select {
    min-width: 0;
    flex: 1;
    padding: 8px 22px 8px 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--cco-ink);
    font: inherit;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
  }

  option {
    background: var(--cco-bg);
    color: var(--cco-ink);
  }
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 130px;
  padding: 0 18px;
  border: 0;
  background: transparent;
  color: var(--cco-cyan);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  :hover:not(:disabled) {
    background: rgba(28, 176, 216, 0.1);
  }

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 720px) {
    min-height: 48px;
    justify-content: flex-start;
    padding: 0 var(--cco-pad);
  }
`;

export const TableWrap = styled.section`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
`;

export const Table = styled.div`
  display: grid;
  width: 100%;
  min-width: 1420px;
  grid-template-columns:
    140px minmax(190px, 0.7fr) minmax(190px, 0.75fr)
    minmax(210px, 0.8fr) 84px 84px minmax(300px, 1.2fr)
    minmax(190px, 0.75fr) minmax(150px, 0.55fr);

  @media (max-width: 720px) {
    display: flex;
    min-width: 0;
    flex-direction: column;
    overflow-x: hidden;
  }
`;

export const Th = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'left' ? 'flex-start' : 'center'};
  padding: 14px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  color: var(--cco-ink-mute);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-align: center;
  text-transform: uppercase;

  @media (max-width: 720px) {
    display: none;
  }
`;

export const Tr = styled.div`
  display: contents;

  :hover .tc {
    background: rgba(255, 255, 255, 0.02);
  }

  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px var(--cco-pad);
    border-bottom: 1px solid var(--cco-line);
  }
`;

export const Tc = styled.div.attrs({ className: 'tc' })`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 16px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  transition: background 120ms ease;

  @media (max-width: 720px) {
    justify-content: flex-start;
    padding: 4px 0;
    border-bottom: 0;

    &::before {
      content: attr(data-label);
      min-width: 104px;
      margin-right: 8px;
      flex-shrink: 0;
      color: var(--cco-ink-mute);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
  }
`;

export const TextCell = styled(Tc)`
  justify-content: flex-start;
  color: var(--cco-ink);
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ValueCell = styled(TextCell)`
  justify-content: center;
  color: ${({ $tone }) =>
    $tone === 'current'
      ? 'var(--cco-green)'
      : $tone === 'previous'
      ? 'var(--cco-yellow, #f5b849)'
      : 'var(--cco-ink-mute)'};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', monospace;
  font-size: 12px;
  text-align: center;
`;

export const DateCell = styled(Tc)`
  color: var(--cco-ink);
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid
    ${(props) =>
      props.$type === 'setpoint'
        ? 'rgba(28, 176, 216, 0.5)'
        : props.$type === 'command'
        ? 'rgba(43, 212, 125, 0.45)'
        : 'rgba(245, 184, 73, 0.5)'};
  border-radius: 999px;
  color: ${(props) =>
    props.$type === 'setpoint'
      ? 'var(--cco-cyan)'
      : props.$type === 'command'
      ? 'var(--cco-green)'
      : 'var(--cco-yellow, #f5b849)'};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const EmptyRow = styled.div`
  grid-column: 1 / -1;
  padding: 60px 0;
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const SkeletonCell = styled(Tc)`
  ::before {
    content: '';
    display: block;
    width: ${(props) => props.$w || '70%'};
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.04) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }

  @media (max-width: 720px) {
    ::before {
      margin-right: 0;
      min-width: 0;
    }
  }
`;

export const Pager = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px var(--cco-pad);
  border-top: 1px solid var(--cco-line);

  .btn,
  .exhibit select {
    height: 34px;
    border: 1px solid var(--cco-line-strong);
    border-radius: 8px;
    background: transparent;
    color: var(--cco-ink);
    font: inherit;
    font-size: 13px;
  }

  .btn {
    padding: 0 14px;
    cursor: pointer;
  }

  .btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .page,
  .exhibit {
    color: var(--cco-ink-mute);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  .exhibit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: 8px;
  }

  .exhibit select {
    padding: 0 8px;
  }

  .exhibit option {
    background: var(--cco-bg);
    color: var(--cco-ink);
  }

  @media (max-width: 720px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;
